import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  type SelectChangeEvent,
  type SxProps,
  type Theme,
} from "~/common/mui";
import { C_OUTLINE } from "~/common/components/icon/Colors";
import { ControlField } from "./ControlField";
import { ControlOptionLabel } from "./ControlOptionLabel";
import type { DropdownSelectControlViewModel } from "./controlViewModel";

export interface DropdownSelectControlProps extends Omit<DropdownSelectControlViewModel, "type" | "controlIndex"> {
  onChange: (value: string) => void;
  appearance?: "default" | "carouselDetail";
  className?: string;
  sx?: SxProps<Theme>;
}

export const DropdownSelectControl = ({
  label,
  hideLabel,
  info,
  helperText,
  error,
  errorText,
  disabled = false,
  value,
  options,
  placeholder,
  standardLabel,
  onChange,
  appearance = "default",
  className,
  sx,
}: DropdownSelectControlProps) => {
  const handleChange = (event: SelectChangeEvent<unknown>) => onChange(event.target.value as string);
  const selectedOption = options.find((option) => option.value === value);
  const select = (
    <FormControl
      fullWidth
      size="small"
      error={Boolean(error)}
      disabled={disabled}
      required={appearance === "carouselDetail"}
      className={appearance === "carouselDetail" ? className : undefined}
      sx={appearance === "carouselDetail" ? sx : undefined}
    >
      {appearance === "carouselDetail" ? <InputLabel>{label}</InputLabel> : null}
      <Select
        value={value ?? ""}
        displayEmpty={appearance === "default"}
        label={appearance === "carouselDetail" ? label : undefined}
        inputProps={{ "aria-label": label }}
        sx={(theme) => ({
          backgroundColor: theme.palette.surfaceContainer.lowest,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: C_OUTLINE },
        })}
        renderValue={() =>
          selectedOption ? (
            <ControlOptionLabel option={selectedOption} standardLabel={standardLabel} />
          ) : appearance === "default" ? (
            <Typography variant="bodyLarge" color="textSecondary">
              {placeholder}
            </Typography>
          ) : undefined
        }
        onChange={handleChange}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
            <ControlOptionLabel option={option} standardLabel={standardLabel} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  if (appearance === "carouselDetail") {
    return select;
  }

  return (
    <ControlField
      label={label}
      hideLabel={hideLabel}
      info={info}
      helperText={helperText}
      error={Boolean(error)}
      errorText={errorText}
      className={className}
      sx={sx}
    >
      {select}
    </ControlField>
  );
};
